import { type FaireReviewEntity } from "@faire/playground-entity-db/entity_types/review";

/**
 * Which of a brand's real reviews are worth putting on its home page.
 *
 * Every review reaching this layer is already a real, verbatim, 5-star one, so
 * this is not a sentiment filter — it decides whether a *true* review is a
 * usable pull-quote. Two things disqualify one, and both were caught by reading
 * the rendered page rather than the data:
 *
 *   Length. Faire's most enthusiastic reviewers write the longest reviews, so
 *   sorting for positivity alone surfaces paragraphs. A seven-line quote stops
 *   being proof read at a glance and becomes homework next to a three-line one.
 *
 *   Naming a colour. A quote about the "pink ruffle robe" beside a photo of the
 *   navy colourway reads as a mistake, and it is not one the copy can fix — the
 *   tile shows the product's hero image, and which colourway that is belongs to
 *   the brand. Nothing in the product data says which gallery image is pink, so
 *   the honest move is to prefer a review that doesn't make a claim the photo
 *   has to live up to (Ryan, 2026-09-17).
 */

/**
 * Characters a full line carries at the module's rendered width — measured, not
 * assumed. At 325px of `type-fs-paragraph` (14px on a 20px line) the four
 * quotes on `/rl/softies-home` ran 65, 104, 217 and 298 characters and wrapped
 * to 2, 3, 5 and 7 lines; this divisor reproduces all four exactly.
 */
const CHARS_PER_LINE = 45;

/** Past this a quote is a paragraph, not a pull-quote. A hard gate: it is a
 * layout guarantee, so the module drops the review rather than the module. */
const MAX_LINES = 5;

/** Where a quote lands as proof at a glance. Short is not automatically
 * better — a two-line quote reads as thin praise next to a three-line one that
 * says something specific. */
const IDEAL_MIN_LINES = 3;
const IDEAL_MAX_LINES = 4;

/**
 * Colour words common in apparel and home copy. Deliberately a plain list and
 * not a regex over every colour in English: a false positive costs one review
 * out of a brand's pool, while a false negative ships the mismatch.
 */
const COLOUR_WORDS = new Set([
  "beige",
  "black",
  "blue",
  "blush",
  "bronze",
  "brown",
  "burgundy",
  "camel",
  "charcoal",
  "cream",
  "crimson",
  "emerald",
  "fuchsia",
  "gold",
  "gray",
  "green",
  "grey",
  "indigo",
  "ivory",
  "khaki",
  "lavender",
  "lilac",
  "magenta",
  "maroon",
  "mauve",
  "mint",
  "navy",
  "olive",
  "orange",
  "peach",
  "pink",
  "purple",
  "red",
  "sage",
  "silver",
  "tan",
  "taupe",
  "teal",
  "turquoise",
  "violet",
  "white",
  "yellow",
]);

export const estimateReviewLines = (text: string): number =>
  Math.max(1, Math.ceil(text.trim().length / CHARS_PER_LINE));

export const reviewNamesAColour = (text: string): boolean =>
  (text.toLowerCase().match(/[a-z]+/g) ?? []).some((word) =>
    COLOUR_WORDS.has(word)
  );

/** A review long enough to be a paragraph can't be laid out around. Everything
 * else is a preference, expressed in the score. */
export const isReviewUsable = (review: FaireReviewEntity): boolean =>
  estimateReviewLines(review.reviewText) <= MAX_LINES;

/**
 * Higher is better. Built from penalties against an ideal rather than as a
 * weighted sum, so the ordering is readable: a review in the ideal band with no
 * colour claim scores 0, and every flaw is a documented number of points.
 */
export const scoreReview = (review: FaireReviewEntity): number => {
  const lines = estimateReviewLines(review.reviewText);

  // Distance outside the ideal band, in lines. Being long is worse than being
  // short: an over-long quote unbalances the row it sits in, where a short one
  // just says less.
  const linesOver = Math.max(0, lines - IDEAL_MAX_LINES);
  const linesUnder = Math.max(0, IDEAL_MIN_LINES - lines);
  const lengthPenalty = linesOver * 3 + linesUnder * 2;

  // Outweighs any length preference: a visible mismatch between the quote and
  // the photo costs more than a quote being a line short.
  const colourPenalty = reviewNamesAColour(review.reviewText) ? 20 : 0;

  return -(lengthPenalty + colourPenalty);
};

/**
 * The best usable review of a product, or `undefined` when it has none. Callers
 * omit the quote in that case — they must never fabricate one or borrow another
 * product's.
 */
export const pickBestReview = (
  reviews: FaireReviewEntity[]
): FaireReviewEntity | undefined =>
  reviews
    .filter(isReviewUsable)
    .sort((left, right) => scoreReview(right) - scoreReview(left))[0];
